import { useEffect, useRef } from "react";
import TiltCard from "@/components/base/TiltCard";
import { SITE_MEDIA } from "@/constants/siteMedia";
import { openLiveDemoChat } from "@/constants/homeSections";

const cards = [
  {
    label: "No judgment",
    icon: "ri-shield-check-line",
    color: "#10b981",
    bg: "#f0fdf4",
    border: "#10b981",
    image: SITE_MEDIA.emotional1,
    desc: "Share anything, freely. Ava listens without criticism, without labels, without judgment.",
  },
  {
    label: "No pressure",
    icon: "ri-leaf-line",
    color: "#7C3AED",
    bg: "#f5f0ff",
    border: "#7C3AED",
    image: SITE_MEDIA.emotional2,
    desc: "No timers, no expectations. Talk when you want, for as long as you need.",
  },
  {
    label: "Just someone who listens",
    icon: "ri-heart-line",
    color: "#e11d48",
    bg: "#fff1f2",
    border: "#e11d48",
    image: SITE_MEDIA.emotional3,
    desc: "Sometimes you don't need solutions. You just need someone to be there — and Ava always is.",
  },
];

export default function EmotionalHit() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = textRef.current;
    const cardsEl = cardsRef.current;
    if (!el || !cardsEl) return;

    el.style.opacity = "0";
    el.style.transform = "translateY(40px)";
    el.style.transition = "opacity 1s ease, transform 1s ease";

    cardsEl.style.opacity = "0";
    cardsEl.style.transform = "translateY(30px)";
    cardsEl.style.transition = "opacity 0.9s ease 0.3s, transform 0.9s ease 0.3s";

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.style.opacity = "1";
            el.style.transform = "none";
            cardsEl.style.opacity = "1";
            cardsEl.style.transform = "none";
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="emotional-hit"
      className="py-14 md:py-20 relative overflow-hidden"
      style={{ background: "linear-gradient(180deg, #f5f0ff 0%, #ede8ff 60%, #f0eeff 100%)" }}
    >
      <style>{`
        @keyframes softGlow {
          0%, 100% { opacity: 0.08; transform: scale(1); }
          50% { opacity: 0.18; transform: scale(1.08); }
        }
      `}</style>

      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute rounded-full"
          style={{
            width: 600,
            height: 600,
            background: "radial-gradient(circle, #A855F7 0%, transparent 70%)",
            top: "-20%",
            right: "-15%",
            animation: "softGlow 5s ease-in-out infinite",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: 400,
            height: 400,
            background: "radial-gradient(circle, #7C3AED 0%, transparent 70%)",
            bottom: "-10%",
            left: "-10%",
            animation: "softGlow 6s ease-in-out infinite 1.5s",
          }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-8">
        <div ref={textRef} className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#7C3AED]/20 bg-white text-[#7C3AED] text-sm font-medium mb-4">
            <i className="ri-chat-heart-line"></i>
            SOLUTION
          </div>

          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1a1040] mb-6 leading-tight">
            Meet Ava.
            <br />
            <span
              className="text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(90deg, #7C3AED, #A855F7)" }}
            >
              Ava is an AI companion designed for real conversations.
            </span>
          </h2>

          <p className="text-[#5a4a8a] text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            Whether you need to vent, reflect, or just not feel alone, Ava is here to listen—anytime you need it.
          </p>
        </div>

        {/* 3 Cards with Tilt */}
        <div ref={cardsRef} className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {cards.map((card, i) => (
            <TiltCard
              key={i}
              tiltAmount={8}
              scale={1.02}
            >
              <div
                className="rounded-3xl overflow-hidden border bg-white cursor-default group"
                style={{ borderColor: card.border + "25" }}
              >
                <div className="w-full h-44 overflow-hidden">
                  <img
                    src={card.image}
                    alt={card.label}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                <div className="p-5">
                  <div
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-3"
                    style={{ background: card.bg, color: card.color }}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${card.icon} text-xs`}></i>
                    </div>
                    {card.label}
                  </div>
                  <p className="text-[#5a4a8a] text-sm leading-relaxed">{card.desc}</p>
                </div>
              </div>
            </TiltCard>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <button
            type="button"
            className="whitespace-nowrap inline-flex items-center gap-3 px-8 py-4 rounded-full text-white font-bold text-base cursor-pointer hover:opacity-90 transition-all hover:scale-105 border-none"
            style={{ background: "linear-gradient(135deg, #7C3AED, #A855F7)" }}
            onClick={() => openLiveDemoChat()}
          >
            <i className="ri-chat-smile-3-line text-xl w-6 h-6 flex items-center justify-center"></i>
            Talk to Ava now
          </button>
        </div>
      </div>
    </section>
  );
}